import {
  FormControl,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  InputAdornment,
  Input,
  Checkbox,
  FormControlLabel,
  Stack,
  Button,
  Menu,
} from '@mui/material';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { alpha, styled } from '@mui/material/styles';

import { visuallyHidden } from '@mui/utils';

import React, { useEffect, useState } from 'react';

import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { AiOutlineSearch } from 'react-icons/ai';

import type { PageInfo } from '@/generated/graphql';

import { ChangePageType } from '@/shared/enums';

import type { ICoordinator, ISelectedUsher, IUsherQueryVariable, IUsherTable } from '@/shared/interfaces';

import { RandomKeyString } from '../services';

import type { SetStateAction } from 'react';

import '../styles/ushers.css';

// components
import { TableBodySkeleton } from '@/components/table/table-body-skeleton';

type Order = 'asc' | 'desc';

interface TablePageLabel {
  start: number;
  end: number;
}

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof IUsherTable) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  coordinator?: ICoordinator;
  isAllSelected: boolean;
  clickSelectAll: (action: boolean) => void;
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof IUsherTable;
  label: string;
  numeric: boolean;
  className: string;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'uniqueCode',
    numeric: false,
    disablePadding: true,
    label: 'Agent Code',
    className: 'agent-code-column',
  },
  {
    id: 'fullName',
    numeric: false,
    disablePadding: true,
    label: 'Full Name',
    className: 'namecolumn',
  },
  {
    id: 'contactNumber',
    numeric: false,
    disablePadding: true,
    label: 'Contact Number',
    className: 'contactnum-column',
  },
  {
    id: 'usherType',
    numeric: false,
    disablePadding: true,
    label: 'Agent Type',
    className: 'agent-type-column',
  },
  {
    id: 'createdAt',
    numeric: false,
    disablePadding: true,
    label: 'Created On',
    className: 'agent-type-column',
  },
  {
    id: 'DTlastTransaction',
    numeric: false,
    disablePadding: true,
    label: 'Last Transaction',
    className: 'agent-type-column',
  },
  {
    id: 'transactionAccess',
    numeric: false,
    disablePadding: true,
    label: 'Bet Access',
    className: 'agent-type-column',
  },
];

const StyledTableRow = styled(TableRow)(({}) => ({
  '& .MuiTableCell-root': {
    fontFamily: 'Open Sans,Roboto',
    fontWeight: 600,
    fontSize: 15,
    pt: 15,
    pb: 15,
    pl: 15,
  },
  '& .MuiTableCell-hover	': {
    backgroundColor: '#C2DAF0',
    border: '0.08em solid #C2DAF0',
  },

  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort, coordinator, isAllSelected, clickSelectAll } = props;
  const createSortHandler = (property: keyof IUsherTable) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead sx={{ borderBottom: '0.08em solid #ccc', boxShadow: '0em 0.18em 0.4em #bbb' }}>
      <TableRow>
        {coordinator && !coordinator.isDefaultOperator && (
          <TableCell>
            <FormControlLabel
              key={RandomKeyString()}
              control={<Checkbox checked={isAllSelected} onChange={() => clickSelectAll(!isAllSelected)} />}
              label=""
            />
          </TableCell>
        )}

        {headCells.map((headCell) => (
          <TableCell
            key={RandomKeyString()}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            style={{ fontWeight: '600', lineHeight: '1.2', color: '#555' }}
            className={headCell.className}
            sx={{ pl: 2, py: 1 }}>
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}>
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}
const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
  const { numSelected, searchHandler, chooseMoreOptions } = props;

  const [searchWord, setSearchWord] = useState<string>();

  const [anchorElProfileOption, setAnchorElProfileOption] = useState<null | HTMLElement>(null);
  const openProfileOption = Boolean(anchorElProfileOption);

  const handleClickProfileOption = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElProfileOption(event.currentTarget);
  };

  const clickOption = (option: string) => {
    chooseMoreOptions(option, true);
    setAnchorElProfileOption(null);
  };

  return (
    <Toolbar
      sx={{
        backgroundColor: '#6591BD',
        color: '#FFFFFF',
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        justifyContent: 'space-between',
      }}>
      <Stack direction={'row'}>
        <Typography sx={{ flex: '1 1 100%' }} variant="subtitle1" id="tableTitle" mr={3}>
          {numSelected ? `${numSelected} Selected` : 'Ushers'}
        </Typography>
        {numSelected > 0 && (
          <div>
            <Button
              id="demo-customized-button"
              aria-controls={openProfileOption ? 'demo-customized-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={openProfileOption ? 'true' : undefined}
              variant="contained"
              disableElevation
              size="small"
              onClick={handleClickProfileOption}
              endIcon={<KeyboardArrowDownIcon />}>
              Options
            </Button>
            <Menu
              id="long-menu"
              anchorEl={anchorElProfileOption}
              open={openProfileOption}
              onClose={() => setAnchorElProfileOption(null)}
              slotProps={{
                paper: {
                  style: {
                    width: '20ch',
                  },
                },
              }}>
              <MenuItem key="Enable Ushers" onClick={() => clickOption('EnableDisable')}>
                Disable Ushers
              </MenuItem>
              <MenuItem key="Remove Ushers" onClick={() => clickOption('Remove')}>
                Remove Ushers
              </MenuItem>
            </Menu>
          </div>
        )}
      </Stack>
      <FormControl sx={{ m: 1, width: '30ch' }} variant="standard">
        <InputLabel htmlFor="standard-adornment-search">Search</InputLabel>
        <Input
          id="standard-adornment-search"
          onChange={(e) => {
            setSearchWord(e.target.value);
          }}
          onKeyDown={(event) => event.key === 'Enter' && searchHandler(searchWord ? searchWord : '')}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={(e) => searchHandler(searchWord ? searchWord : '')}
                // onMouseDown={handleMouseDownPassword}
              >
                {/* {showPassword ? <VisibilityOff /> : <Visibility />} */}
                <AiOutlineSearch />
              </IconButton>
            </InputAdornment>
          }
        />
      </FormControl>
    </Toolbar>
  );
};
interface lUshersProps {
  checkDataEvent: (index: number, isCheck: boolean, usherId: string) => void;
  checkedStates: boolean[];
  setCheckedStates: React.Dispatch<SetStateAction<boolean[]>>;
  rows: readonly IUsherTable[];
  setUsherQueryVariable: React.Dispatch<SetStateAction<IUsherQueryVariable>>;
  usherQueryVariable: IUsherQueryVariable;
  totalCount: number;
  pageInfo: PageInfo | undefined;

  usherEvent: (usherId: string, open: boolean, eventName: string) => void;
  getUsherLoading: boolean;
  coordinator?: ICoordinator;

  setSelectedUshers: React.Dispatch<React.SetStateAction<ISelectedUsher[]>>;
  selectedUshers: ISelectedUsher[];
  chooseMoreOptions: (option: string, multipleUsherIsDisabledAction: boolean) => void;
}

interface EnhancedTableToolbarProps {
  numSelected: number;
  searchHandler: (value: string) => void;
  chooseMoreOptions: (option: string, multipleUsherIsDisabledAction: boolean) => void;
}

export function CoordinatorTableUshers({
  checkDataEvent,
  checkedStates,
  setCheckedStates,
  rows,
  usherQueryVariable,
  setUsherQueryVariable,
  totalCount,
  pageInfo,
  getUsherLoading,
  coordinator,
  setSelectedUshers,
  selectedUshers,
  chooseMoreOptions,
}: lUshersProps) {
  const [search, setSearch] = useState('');

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof IUsherTable>('firstName');
  const [page] = useState(0);
  const [dense] = useState(true);
  const [rowsPerPage] = useState(5);
  const [pageLabel, setPageLabel] = useState<TablePageLabel>({
    start: 1,
    end: usherQueryVariable.first,
  });
  const [isAllSelected, setIsAllSelected] = useState(false);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof IUsherTable) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const searchHandler = (value: string) => {
    setSearch(value);

    console.log(value, '==');

    setUsherQueryVariable({ ...usherQueryVariable, filterBy: { ...usherQueryVariable.filterBy, search: value } });
  };

  const handleChangePage = (type: ChangePageType) => {
    if (type === ChangePageType.NEXT && pageInfo?.endCursor) {
      // next
      setUsherQueryVariable({
        ...usherQueryVariable,
        after: pageInfo.endCursor,
        before: undefined,
      });
      let end = pageLabel.end + usherQueryVariable.first;
      if (end > totalCount) {
        end = totalCount;
      }
      setPageLabel({ start: pageLabel.start + usherQueryVariable.first, end });
    } else if (type === ChangePageType.PREV && pageInfo?.startCursor) {
      // prev
      setUsherQueryVariable({
        ...usherQueryVariable,
        before: pageInfo.startCursor,
        after: undefined,
      });
      let end = pageLabel.end - usherQueryVariable.first;
      if (end > totalCount) {
        end = totalCount;
      }
      setPageLabel({ start: pageLabel.start - usherQueryVariable.first, end });
    }
  };

  const handleChangeRowsPerPage = (value: string | number) => {
    const intValue = parseInt(value.toString(), 10);

    setUsherQueryVariable({
      ...usherQueryVariable,
      first: intValue,
    });
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const handleChange = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    checkDataEvent(index, event.target.checked, event.target.value);
    const newState = [...checkedStates];
    newState[index] = event.target.checked;
    setCheckedStates(newState);
  };

  const handleChangeCheckbox = (id: string) => {
    const index = rows.findIndex((x) => x.id === id);

    if (index !== -1) {
      const foundData = rows[index];
      const foundData2 = selectedUshers.some((x) => x.id === id);

      if (!foundData2) {
        setSelectedUshers((prev) => [...prev, ...[{ id: foundData.id, name: foundData.fullName, uniqueCode: foundData.uniqueCode }]]);
      } else {
        setSelectedUshers((prev) => prev.filter((x) => x.id !== id));
      }
    }
    setIsAllSelected(false);
  };

  const clickSelectAll = () => {
    if (isAllSelected === false) {
      setSelectedUshers(rows.map((x) => ({ id: x.id, name: x.fullName, uniqueCode: x.uniqueCode })));
    } else {
      setSelectedUshers([]);
    }

    setIsAllSelected((prev) => !prev);
  };

  useEffect(() => {
    if (selectedUshers.length === 0) {
      setIsAllSelected(false);
    }
  }, [selectedUshers]);

  return (
    <Box sx={{ width: '100%', mt: 3 }}>
      <Paper
        sx={{
          width: '100%',
          mb: 2,
          backgroundColor: '#ffffff',
        }}>
        <EnhancedTableToolbar numSelected={selectedUshers.length} searchHandler={searchHandler} chooseMoreOptions={chooseMoreOptions} />
        <TableContainer>
          <Table
            sx={{
              minWidth: 850,
            }}
            aria-labelledby="tableTitle"
            size={dense ? 'small' : 'medium'}>
            <EnhancedTableHead
              numSelected={selectedUshers.length}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
              coordinator={coordinator}
              isAllSelected={isAllSelected}
              clickSelectAll={clickSelectAll}
            />
            {getUsherLoading ? (
              <TableBodySkeleton row={10} column={9} />
            ) : (
              <TableBody>
                {rows.map((row, index) => {
                  const isItemSelected = selectedUshers.some((x) => x.id === row.id);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <StyledTableRow
                      sx={{
                        className: 'ushers-table-row',
                        backgroundColor: row.isDisabled === true ? '#dedede' : 'initial',
                        opacity: row.isDisabled === true ? '0.75' : 'inherit',
                        borderBottom: 1.1,
                        borderBottomColor: '#9F9F9F',
                      }}
                      // hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={RandomKeyString()}
                      selected={isItemSelected}>
                      {coordinator && !coordinator.isDefaultOperator && (
                        <TableCell key={RandomKeyString()}>
                          <FormControlLabel
                            key={RandomKeyString()}
                            value={row?.id}
                            control={<Checkbox checked={isItemSelected} onChange={() => handleChangeCheckbox(row.id)} />}
                            label=""
                          />
                        </TableCell>
                      )}

                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        padding="none"
                        sx={{
                          pl: 2,
                        }}>
                        {row.uniqueCode}
                      </TableCell>
                      <TableCell align="left" style={{ fontWeight: '600', color: '#6b6b6b' }}>
                        {row.firstName} {row.lastName}
                      </TableCell>
                      <TableCell align="left">{row.contactNumber}</TableCell>
                      <TableCell align="left">{row.usherType}</TableCell>
                      <TableCell align="left" style={{ fontWeight: '600', color: '#6b6b6b' }}>
                        {row.createdAt}
                      </TableCell>
                      <TableCell align="left">{row.DTlastTransaction}</TableCell>
                      <TableCell align="left">{row.transactionAccess.replace('_ACCESS', ' ').replace(/_/g, ' ')}</TableCell>
                    </StyledTableRow>
                  );
                })}
                {emptyRows > 0 && (
                  <TableRow
                    style={{
                      height: (dense ? 33 : 53) * emptyRows,
                    }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            )}
          </Table>
        </TableContainer>
        <Box
          sx={{
            backgroundColor: '#6591BD',
            color: '#FFFFFF',
            mt: 1,
            justifyContent: 'end',
            alignItems: 'center',
            display: 'flex',
          }}>
          <Typography variant="body2" mr={4} gutterBottom>
            Rows per page:
          </Typography>
          <FormControl variant="standard" sx={{ m: 1, minWidth: 50 }}>
            <Select
              onChange={(e) => {
                handleChangeRowsPerPage(e.target.value);
              }}
              labelId="demo-simple-select-standard-label"
              id="demo-simple-select-standard"
              value={usherQueryVariable.first}
              label="Row">
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" mx={4} gutterBottom>
            {pageLabel.start} - {pageLabel.end} of {totalCount}
          </Typography>
          <IconButton
            onClick={() => handleChangePage(ChangePageType.PREV)}
            disabled={pageInfo?.hasPreviousPage ? false : true}
            size={'small'}
            sx={{ mr: 2 }}
            aria-label="prev">
            <IoIosArrowBack />
          </IconButton>
          <IconButton
            onClick={() => handleChangePage(ChangePageType.NEXT)}
            disabled={pageInfo?.hasNextPage ? false : true}
            size={'small'}
            aria-label="next">
            <IoIosArrowForward />
          </IconButton>
        </Box>
      </Paper>
    </Box>
  );
}
